import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useUser } from '@clerk/tanstack-react-start';
import { useQuery } from 'convex/react';
import { Button, FragmentsLogo, Text } from '@fragments-sdk/ui';
import { api, type Id } from '@/convex';
import { hasFeature, type PlanId } from '../../convex/billing/plans';

export const Route = createFileRoute('/cli-auth')({
  component: CliAuthPage,
  validateSearch: (search: Record<string, unknown>) => ({
    port:
      typeof search.port === 'string'
        ? search.port
        : typeof search.port === 'number'
          ? String(search.port)
          : undefined,
    nonce: typeof search.nonce === 'string' ? search.nonce : undefined,
  }),
});

function isValidPort(value: string | undefined): value is string {
  if (!value) return false;
  const port = Number(value);
  return Number.isInteger(port) && port > 0 && port <= 65535;
}

function CliAuthPage() {
  const { port, nonce } = Route.useSearch();
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();
  const valid = isValidPort(port) && Boolean(nonce);

  const memberships = useQuery(
    api.memberships.listByUser,
    user?.id ? { clerkUserId: user.id } : 'skip',
  );
  const adminMembership = memberships?.find(
    (entry) =>
      entry.membership.role === 'owner' || entry.membership.role === 'admin',
  );
  // The legacy CLI browser-auth flow reveals the org-broad key. With the
  // hosted loop gated behind a paid plan (brief 08), `revealApiKey` throws
  // FEATURE_NOT_AVAILABLE on Free — so gate the query and degrade gracefully
  // below instead of crashing the page. New installs use `cli init --token`.
  const hasMachineApi = adminMembership
    ? hasFeature((adminMembership.org.plan ?? 'free') as PlanId, 'apiAccess')
    : false;
  const apiKey = useQuery(
    api.orgs.revealApiKey,
    adminMembership && hasMachineApi
      ? { orgId: adminMembership.org._id as Id<'orgs'> }
      : 'skip',
  );

  React.useEffect(() => {
    if (!valid || !isLoaded) return;
    if (!user) {
      navigate({
        to: '/sign-in',
        search: { cli_port: Number(port), cli_nonce: nonce },
        replace: true,
      });
    }
  }, [isLoaded, navigate, nonce, port, user, valid]);

  React.useEffect(() => {
    if (!valid || !apiKey || !adminMembership) return;
    const callback = new URL(`http://127.0.0.1:${port}/callback`);
    callback.searchParams.set('key', apiKey.apiKey);
    callback.searchParams.set('org', adminMembership.org.name);
    callback.searchParams.set('nonce', nonce!);
    window.location.assign(callback.toString());
  }, [adminMembership, apiKey, nonce, port, valid]);

  if (!valid) {
    return (
      <Shell>
        <Text as="h1" size="xl" weight="bold">
          CLI browser auth has moved
        </Text>
        <Text color="secondary">
          Copy the scoped setup token from onboarding and run{' '}
          <code>npx @fragments-sdk/cli init --token=fc_...</code>.
        </Text>
      </Shell>
    );
  }

  if (
    !isLoaded ||
    !user ||
    memberships === undefined ||
    (adminMembership && hasMachineApi && apiKey === undefined)
  ) {
    return (
      <Shell>
        <Text as="h1" size="xl" weight="bold">
          Sign in to authorize CLI
        </Text>
        <Text color="secondary">
          Sign in to connect your terminal to Fragments Cloud.
        </Text>
      </Shell>
    );
  }

  if (!adminMembership) {
    return (
      <Shell>
        <Text as="h1" size="xl" weight="bold">
          Admin Access Required
        </Text>
        <Text color="secondary">
          Ask an owner or admin to authorize the CLI for this workspace.
        </Text>
        <Button variant="outline" onClick={() => navigate({ to: '/' })}>
          Back to dashboard
        </Button>
      </Shell>
    );
  }

  if (!hasMachineApi) {
    return (
      <Shell>
        <Text as="h1" size="xl" weight="bold">
          CLI browser auth requires Pro
        </Text>
        <Text color="secondary">
          Your workspace is on Free. Connect the CLI with a scoped setup token
          instead — <code>npx @fragments-sdk/cli init --token=fc_...</code> —
          or upgrade to Pro for the hosted machine API.
        </Text>
        <Button variant="outline" onClick={() => navigate({ to: '/' })}>
          Back to dashboard
        </Button>
      </Shell>
    );
  }

  return (
    <Shell>
      <Text as="h1" size="xl" weight="bold">
        Authorizing CLI
      </Text>
      <Text color="secondary">
        Sending your Fragments Cloud key to the local terminal callback.
      </Text>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        background: 'var(--fui-bg-primary)',
        color: 'var(--fui-fg-primary)',
      }}
    >
      <section
        style={{
          display: 'grid',
          gap: 16,
          maxWidth: 420,
          textAlign: 'center',
          justifyItems: 'center',
        }}
      >
        <FragmentsLogo size={40} />
        {children}
      </section>
    </main>
  );
}
